import {
  forwardRef,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  type ReactNode,
} from "react";
import { Icon, type IconName } from "./Icon";

type Size = "sm" | "md" | "lg";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: IconName;
  suffix?: ReactNode;
  inputSize?: Size;
  /** layout: stack the label above the field (default) or inline */
  hideWrapper?: boolean;
}

const HEIGHTS: Record<Size, string> = {
  sm: "h-8 text-xs",
  md: "h-9 text-sm",
  lg: "h-11 text-sm",
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    error,
    icon,
    suffix,
    className = "",
    id,
    inputSize = "md",
    hideWrapper,
    required,
    ...rest
  },
  ref,
) {
  const inputId =
    id ?? `input-${label?.replace(/\s+/g, "-").toLowerCase() ?? "field"}`;
  const inner = (
    <div
      className={`flex items-center rounded-lg border border-border-strong bg-elevated px-3 transition focus-within:border-text focus-within:shadow-[0_0_0_3px_rgba(20,18,13,0.06)] dark:focus-within:shadow-[0_0_0_3px_rgba(248,244,233,0.08)] ${HEIGHTS[inputSize]}`}
    >
      {icon && (
        <Icon name={icon} size={15} className="text-muted mr-2 shrink-0" />
      )}
      <input
        id={inputId}
        ref={ref}
        required={required}
        className={`flex-1 bg-transparent outline-none text-text placeholder:text-muted ${className}`}
        {...rest}
      />
      {suffix && <div className="ml-2 shrink-0 text-muted">{suffix}</div>}
    </div>
  );
  if (hideWrapper) return inner;
  return (
    <div className="mb-3">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-xs font-medium text-muted mb-1.5"
        >
          {label}
          {required && <span className="ml-1 text-danger">*</span>}
        </label>
      )}
      {inner}
      {error && <p className="text-xs text-danger mt-1">{error}</p>}
    </div>
  );
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hideWrapper?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    {
      label,
      error,
      className = "",
      id,
      rows = 4,
      hideWrapper,
      required,
      ...rest
    },
    ref,
  ) {
    const inputId =
      id ?? `textarea-${label?.replace(/\s+/g, "-").toLowerCase() ?? "field"}`;
    const inner = (
      <textarea
        id={inputId}
        ref={ref}
        rows={rows}
        required={required}
        className={`w-full px-3 py-2 rounded-lg border border-border-strong bg-elevated text-text text-sm placeholder:text-muted focus:outline-none focus:border-text focus:shadow-[0_0_0_3px_rgba(20,18,13,0.06)] dark:focus:shadow-[0_0_0_3px_rgba(248,244,233,0.08)] ${className}`}
        {...rest}
      />
    );

    if (hideWrapper) return inner;

    return (
      <div className="mb-3">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-xs font-medium text-muted mb-1.5"
          >
            {label}
            {required && <span className="ml-1 text-danger">*</span>}
          </label>
        )}
        {inner}
        {error && <p className="text-xs text-danger mt-1">{error}</p>}
      </div>
    );
  },
);
